/* eslint-disable @typescript-eslint/naming-convention */
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { withLoadingReducer } from 'utils/reducerHandler'
import {
  GetProductInventoriesParamsType,
  GetProductInventoriesResponseType,
  ProductInventoryItemType,
} from 'features/Product/services/inventory'
import { GetCloudKitchenScheduleProductHubResponseType } from 'features/Product/services/cloudKitchen'
import {
  SLICE_NAME,
  fetchGetHubLocations,
  fetchGetProductHub,
  fetchPutInventoryLocation,
  fetchPutInventoryLocationStatus,
  fetchGetCloudKitchenProductHub,
  fetchPostCloudKitchenSchedule,
} from './thunk'

export type ConstantType = {
  id: number
  name: string
}

export type HubStateType = {
  isLoading: boolean
  query: GetProductInventoriesParamsType
  constants: {
    locations: ConstantType[]
  }
  pagination: {
    page_size: number
    page_index: number
    direction: 'asc' | 'desc'
    sort: string
  }
  data: (ProductInventoryItemType & {
    cid: string
  })[]
  popup: {
    cloudKitchenSchedule: {
      isOpen: boolean
      details: Nullable<GetProductInventoriesResponseType['data'][0]>
      item: Nullable<GetCloudKitchenScheduleProductHubResponseType['data']>
    }
  }
}

const initialState: HubStateType = {
  isLoading: false,
  query: {
    'pagination.page_index': 1,
    'filter.cloud_kitchen': '',
  },
  constants: {
    locations: [],
  },
  pagination: {
    page_size: 20,
    page_index: 1,
    direction: 'asc',
    sort: '',
  },
  data: [],
  popup: {
    cloudKitchenSchedule: {
      isOpen: false,
      details: null,
      item: null,
    },
  },
}

const hubSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setOpenPopupCloudKitchenSchedule: (
      state,
      action: {
        payload: {
          isOpen: boolean
          item: Nullable<GetCloudKitchenScheduleProductHubResponseType['data']>
          details: Nullable<GetProductInventoriesResponseType['data'][0]>
        }
      },
    ) => {
      state.popup.cloudKitchenSchedule.isOpen = action.payload.isOpen
      state.popup.cloudKitchenSchedule.details = action.payload.details
      state.popup.cloudKitchenSchedule.item = action.payload.item
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<HubStateType>) => {
      builder
        .addCase(fetchGetProductHub.pending, (state, action) => {
          state.query = {
            ...state.query,
            ...action.meta.arg,
          }
        })
        .addCase(fetchGetProductHub.fulfilled, (state, action) => {
          const {
            data,
            pagination: { direction, page_index, page_size, sort },
          } = action.payload

          state.data = data.map((item) => ({
            ...item,
            cid: uuidv4(),
          }))

          state.pagination = {
            page_size,
            direction,
            page_index,
            sort,
          }
        })
        // =====
        .addCase(fetchGetHubLocations.fulfilled, (state, action) => {
          state.constants.locations = action.payload.map((loc) => ({
            id: loc.location_id,
            name: loc.location_name,
          }))
        })
        // =====
        .addCase(fetchGetCloudKitchenProductHub.fulfilled, (state, action) => {
          state.popup.cloudKitchenSchedule.item = action.payload.data
        })
    },
    [
      fetchGetProductHub,
      fetchPutInventoryLocationStatus,
      fetchPutInventoryLocation,
      fetchGetCloudKitchenProductHub,
      fetchPostCloudKitchenSchedule,
    ],
  ),
})

export const { reset, setOpenPopupCloudKitchenSchedule } = hubSlice.actions
export default hubSlice.reducer
